import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Phone, Mail, MapPin, Clock, Send } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import SEO from '../components/SEO';

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Create email body with form data
    const emailBody = `
Naam: ${formData.name}
Email: ${formData.email}
Telefoon: ${formData.phone || 'Niet opgegeven'}

Bericht:
${formData.message}
    `.trim();
    
    // Open email client with pre-filled data
    const mailtoLink = `mailto:info@kscare.nl?subject=${encodeURIComponent(formData.subject)}&body=${encodeURIComponent(emailBody)}`;
    window.location.href = mailtoLink;
    
    toast({
      title: "Email geopend!",
      description: "Verstuur de email via uw email programma.",
    });
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen">
      <SEO 
        title="Contact - Bel 06-19199023"
        description="Neem contact op met KS Care. Bel 06-19199023 of mail info@kscare.nl. Bezoek ons: Delweide 34, 6118 AH Nieuwstadt."
        keywords="contact, telefoon, email, adres, Nieuwstadt, intake, aanmelden"
        
      />
      <Header />
      
      {/* Hero */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h1 
              className="text-5xl md:text-6xl font-bold text-[#1A4D4D] mb-6"
              style={{ fontFamily: 'Fredoka, Quicksand, sans-serif' }}
            >
              Contact
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              Heeft u vragen of wilt u een intake plannen? Neem gerust contact met ons op!
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            <Card className="p-6 rounded-3xl border-none shadow-lg text-center">
              <div className="w-14 h-14 bg-gradient-to-br from-[#FFE8E0] to-[#FFD0C0] rounded-full flex items-center justify-center mx-auto mb-4">
                <Phone className="w-7 h-7 text-[#FF6347]" />
              </div>
              <h3 className="text-lg font-bold text-[#1A4D4D] mb-2">Telefoon</h3>
              <a href="tel:0619199023" className="text-gray-600 hover:text-[#FF6347] transition-colors">
                06-19199023
              </a>
            </Card>

            <Card className="p-6 rounded-3xl border-none shadow-lg text-center">
              <div className="w-14 h-14 bg-gradient-to-br from-[#E0F2F7] to-[#B3E5FC] rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="w-7 h-7 text-[#5DADE2]" />
              </div>
              <h3 className="text-lg font-bold text-[#1A4D4D] mb-2">Email</h3>
              <a href="mailto:info@kscare.nl" className="text-gray-600 hover:text-[#FF6347] transition-colors break-all">
                info@kscare.nl
              </a>
            </Card>

            <Card className="p-6 rounded-3xl border-none shadow-lg text-center">
              <div className="w-14 h-14 bg-gradient-to-br from-[#FFF4E0] to-[#FFE0B2] rounded-full flex items-center justify-center mx-auto mb-4">
                <MapPin className="w-7 h-7 text-[#F39C12]" />
              </div>
              <h3 className="text-lg font-bold text-[#1A4D4D] mb-2">Locatie</h3>
              <p className="text-gray-600 text-sm">Delweide 34<br/>6118 AH Nieuwstadt</p>
            </Card>

            <Card className="p-6 rounded-3xl border-none shadow-lg text-center">
              <div className="w-14 h-14 bg-gradient-to-br from-[#E8F5E9] to-[#C8E6C9] rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="w-7 h-7 text-[#52BE80]" />
              </div>
              <h3 className="text-lg font-bold text-[#1A4D4D] mb-2">Bereikbaarheid</h3>
              <p className="text-gray-600 text-sm">08:00 - 20:00<br/>In overleg kunnen tijden worden aangepast</p>
            </Card>
          </div>

          {/* Contact Form */}
          <div className="max-w-3xl mx-auto">
            <Card className="p-8 md:p-12 rounded-3xl border-none shadow-xl">
              <h2 className="text-3xl font-bold text-[#1A4D4D] mb-6 text-center" style={{ fontFamily: 'Fredoka, Quicksand, sans-serif' }}>
                Stuur ons een bericht
              </h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Naam *
                    </label>
                    <Input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full rounded-xl border-gray-300"
                      placeholder="Uw naam"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email *
                    </label>
                    <Input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full rounded-xl border-gray-300"
                      placeholder="uw@email.nl"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Telefoon
                    </label>
                    <Input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full rounded-xl border-gray-300"
                      placeholder="06-12345678"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Onderwerp *
                    </label>
                    <Input
                      type="text"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className="w-full rounded-xl border-gray-300"
                      placeholder="Bijv: Vraag over dagopvang"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Bericht *
                  </label>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    className="w-full rounded-xl border-gray-300"
                    placeholder="Vertel ons waar we u mee kunnen helpen..."
                  />
                </div>

                <Button 
                  type="submit"
                  className="w-full bg-[#FF6347] hover:bg-[#FF5533] text-white rounded-full px-8 py-6 text-lg"
                >
                  <Send className="w-5 h-5 mr-2" />
                  Verstuur bericht
                </Button>
              </form>
            </Card>
          </div>

          {/* Additional Info */}
          <div className="mt-16 max-w-3xl mx-auto bg-gradient-to-br from-gray-50 to-white rounded-3xl p-8">
            <h3 className="text-2xl font-bold text-[#1A4D4D] mb-4 text-center">Intake & Kennismaking</h3>
            <p className="text-gray-600 text-center mb-6">
              Voor een persoonlijke intake en rondleiding in onze aanleunwoning kunt u telefonisch of via email een afspraak maken. We nemen graag de tijd om kennis te maken en al uw vragen te beantwoorden.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button 
                className="bg-[#1A4D4D] hover:bg-[#0F3838] text-white rounded-full px-6"
                onClick={() => window.location.href = 'tel:0619199023'}
              >
                <Phone className="w-4 h-4 mr-2" />
                Bel nu
              </Button>
              <Button 
                variant="outline"
                className="border-2 border-[#1A4D4D] text-[#1A4D4D] hover:bg-[#1A4D4D] hover:text-white rounded-full px-6"
                onClick={() => window.location.href = 'mailto:info@kscare.nl'}
              >
                <Mail className="w-4 h-4 mr-2" />
                Email ons
              </Button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Contact;